import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Search, Plus, Edit, Trash2, Building2, Globe, Shield, AlertCircle,
  Mail, Phone, MapPin, FileText, Save, X, CheckCircle2, Ban
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const h = React.createElement;

const emptyVendor = () => ({
  name: "", vendor_code: "", category: "DOMESTIC", vendor_type: "OEM",
  address: "", city: "", state: "", country: "India", pincode: "",
  contact_person: "", phone: "", email: "", website: "",
  gst_number: "", pan_number: "", msme_status: "NONE", msme_certificate: "",
  bank_name: "", bank_account: "", ifsc_code: "",
  is_blacklisted: false, blacklist_reason: "", notes: ""
});

function VendorManagement() {
  const { getAuthHeader } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterBlacklist, setFilterBlacklist] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyVendor());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCategory) params.category = filterCategory;
      if (filterBlacklist) params.blacklisted = filterBlacklist === "yes";
      const r = await axios.get(`${API_URL}/vendors`, { ...getAuthHeader(), params });
      setVendors(r.data);
    } catch { toast.error("Failed to load vendors"); }
    finally { setLoading(false); }
  }, [search, filterCategory, filterBlacklist]);

  useEffect(() => {
    const t = setTimeout(fetchVendors, 250);
    return () => clearTimeout(t);
  }, [fetchVendors]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { setForm(emptyVendor()); setEditingId(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...emptyVendor(), ...v }); setEditingId(v.id); setOpen(true); };

  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Vendor name is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/vendors/${editingId}`, form, getAuthHeader());
        toast.success("Vendor updated");
      } else {
        await axios.post(`${API_URL}/vendors`, form, getAuthHeader());
        toast.success("Vendor created");
      }
      setOpen(false);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/vendors/${v.id}`, getAuthHeader());
      toast.success("Vendor deleted");
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const stats = {
    total: vendors.length,
    domestic: vendors.filter(v => v.category === "DOMESTIC").length,
    global: vendors.filter(v => v.category === "GLOBAL").length,
    msme: vendors.filter(v => v.msme_status && v.msme_status !== "NONE").length,
    blacklisted: vendors.filter(v => v.is_blacklisted).length,
  };

  // Form field helper
  const field = (label, content, span = 1) =>
    h("div", { className: span === 2 ? "md:col-span-2" : (span === 3 ? "md:col-span-3" : "") },
      h(Label, { className: "text-xs font-medium text-slate-600" }, label),
      h("div", { className: "mt-1.5" }, content)
    );

  const sel = (value, onChange, options) => h("select", { value, onChange: e => onChange(e.target.value), className: "w-full h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" },
    options.map(o => h("option", { key: o.value, value: o.value }, o.label))
  );

  return h("div", { className: "space-y-6", "data-testid": "vendor-management-page" },
    // Header
    h("div", { className: "flex flex-col md:flex-row md:items-center md:justify-between gap-4" },
      h("div", null,
        h("h1", { className: "text-2xl font-bold text-slate-900 tracking-tight" }, "Vendor Management"),
        h("p", { className: "text-slate-500 mt-1" }, "Manage vendor master data \u2014 domestic & global suppliers, MSME status, blacklisting")
      ),
      h(Button, { onClick: openNew, className: "bg-slate-900 hover:bg-slate-800", "data-testid": "new-vendor-btn" },
        h(Plus, { className: "w-4 h-4 mr-2" }), "Add Vendor"
      )
    ),

    // Stats
    h("div", { className: "grid grid-cols-2 md:grid-cols-5 gap-3" },
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(Building2, { className: "w-3.5 h-3.5" }), "Total"),
        h("p", { className: "text-2xl font-bold text-slate-900 mt-1" }, stats.total)
      ),
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(MapPin, { className: "w-3.5 h-3.5" }), "Domestic"),
        h("p", { className: "text-2xl font-bold text-blue-700 mt-1" }, stats.domestic)
      ),
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(Globe, { className: "w-3.5 h-3.5" }), "Global"),
        h("p", { className: "text-2xl font-bold text-violet-700 mt-1" }, stats.global)
      ),
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(Shield, { className: "w-3.5 h-3.5" }), "MSME"),
        h("p", { className: "text-2xl font-bold text-emerald-700 mt-1" }, stats.msme)
      ),
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(Ban, { className: "w-3.5 h-3.5" }), "Blacklisted"),
        h("p", { className: "text-2xl font-bold text-rose-700 mt-1" }, stats.blacklisted)
      )
    ),

    // Filters
    h(Card, null, h(CardContent, { className: "p-4" },
      h("div", { className: "flex flex-col md:flex-row gap-3" },
        h("div", { className: "relative flex-1" },
          h(Search, { className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" }),
          h(Input, { placeholder: "Search by name, code, GST, email...", value: search, onChange: e => setSearch(e.target.value), className: "pl-10", "data-testid": "vendor-search" })
        ),
        h("select", { value: filterCategory, onChange: e => setFilterCategory(e.target.value), className: "h-10 px-3 rounded-md border border-slate-200 text-sm bg-white min-w-[140px]" },
          h("option", { value: "" }, "All Categories"),
          h("option", { value: "DOMESTIC" }, "Domestic"),
          h("option", { value: "GLOBAL" }, "Global")
        ),
        h("select", { value: filterBlacklist, onChange: e => setFilterBlacklist(e.target.value), className: "h-10 px-3 rounded-md border border-slate-200 text-sm bg-white min-w-[140px]" },
          h("option", { value: "" }, "All Status"),
          h("option", { value: "no" }, "Active Only"),
          h("option", { value: "yes" }, "Blacklisted")
        )
      )
    )),

    // Vendors Table
    h(Card, null, h(CardContent, { className: "p-0" },
      loading ? h("div", { className: "p-8 text-center text-slate-400" }, "Loading vendors...") :
      vendors.length > 0 ? h("div", { className: "overflow-x-auto" }, h(Table, null,
        h(TableHeader, null, h(TableRow, { className: "bg-slate-50/80" },
          h(TableHead, { className: "font-semibold" }, "Code"),
          h(TableHead, { className: "font-semibold" }, "Name"),
          h(TableHead, { className: "font-semibold" }, "Category"),
          h(TableHead, { className: "font-semibold" }, "GST"),
          h(TableHead, { className: "font-semibold" }, "Contact"),
          h(TableHead, { className: "font-semibold" }, "Status"),
          h(TableHead, { className: "w-24 text-right" }, "Actions")
        )),
        h(TableBody, null, vendors.map(v => h(TableRow, { key: v.id, className: "hover:bg-slate-50/50", "data-testid": "vendor-row-" + v.id },
          h(TableCell, { className: "font-mono text-xs font-semibold text-slate-700" }, v.vendor_code),
          h(TableCell, null,
            h("div", { className: "font-semibold text-sm text-slate-900" }, v.name),
            v.city && h("p", { className: "text-xs text-slate-500" }, [v.city, v.state, v.country].filter(Boolean).join(", "))
          ),
          h(TableCell, null,
            h(Badge, { className: "text-xs " + (v.category === "GLOBAL" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-blue-50 text-blue-700 border-blue-200") }, v.category)
          ),
          h(TableCell, { className: "text-xs font-mono text-slate-600" }, v.gst_number || "\u2014"),
          h(TableCell, null,
            h("div", { className: "text-xs text-slate-600 space-y-0.5" },
              v.contact_person && h("div", { className: "font-medium text-slate-700" }, v.contact_person),
              v.email && h("div", { className: "flex items-center gap-1" }, h(Mail, { className: "w-3 h-3" }), v.email),
              v.phone && h("div", { className: "flex items-center gap-1" }, h(Phone, { className: "w-3 h-3" }), v.phone)
            )
          ),
          h(TableCell, null,
            h("div", { className: "flex flex-col gap-1" },
              v.is_blacklisted ? h(Badge, { className: "bg-rose-50 text-rose-700 border-rose-200 text-xs w-fit" }, h(Ban, { className: "w-3 h-3 mr-1" }), "Blacklisted") :
                h(Badge, { className: "bg-emerald-50 text-emerald-700 border-emerald-200 text-xs w-fit" }, h(CheckCircle2, { className: "w-3 h-3 mr-1" }), "Active"),
              v.msme_status && v.msme_status !== "NONE" && h(Badge, { variant: "outline", className: "text-xs w-fit" }, "MSME: ", v.msme_status)
            )
          ),
          h(TableCell, { className: "text-right" },
            h("div", { className: "flex items-center justify-end gap-1" },
              h(Button, { variant: "ghost", size: "icon", className: "h-7 w-7", onClick: () => openEdit(v), "data-testid": "edit-vendor-" + v.id }, h(Edit, { className: "w-3.5 h-3.5 text-blue-600" })),
              h(Button, { variant: "ghost", size: "icon", className: "h-7 w-7", onClick: () => remove(v), "data-testid": "delete-vendor-" + v.id }, h(Trash2, { className: "w-3.5 h-3.5 text-rose-500" }))
            )
          )
        )))
      )) : h("div", { className: "p-16 text-center" },
        h(Building2, { className: "w-14 h-14 text-slate-200 mx-auto mb-4" }),
        h("p", { className: "text-slate-600 font-medium" }, "No vendors found"),
        h("p", { className: "text-sm text-slate-400 mt-1" }, search ? "Try a different search" : "Add your first vendor to get started")
      )
    )),

    // Add/Edit Dialog
    h(Dialog, { open, onOpenChange: setOpen },
      h(DialogContent, { className: "max-w-3xl max-h-[90vh] overflow-y-auto" },
        h(DialogHeader, null, h(DialogTitle, null, editingId ? "Edit Vendor" : "Add New Vendor")),
        h("div", { className: "space-y-5 py-2" },
          // Identity
          h("div", { className: "space-y-3" },
            h("h3", { className: "text-xs font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Identity"),
            h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
              field("Vendor Name *", h(Input, { value: form.name, onChange: e => set("name", e.target.value), placeholder: "ABC Technologies Pvt Ltd", "data-testid": "v-name" })),
              field("Vendor Code", h(Input, { value: form.vendor_code, onChange: e => set("vendor_code", e.target.value), placeholder: "Auto-generated if empty" })),
              field("Category", sel(form.category, v => set("category", v), [{ value: "DOMESTIC", label: "Domestic" }, { value: "GLOBAL", label: "Global" }])),
              field("Vendor Type", sel(form.vendor_type, v => set("vendor_type", v), [
                { value: "OEM", label: "OEM (Manufacturer)" },
                { value: "AUTHORIZED_DEALER", label: "Authorized Dealer" },
                { value: "DISTRIBUTOR", label: "Distributor" },
                { value: "SERVICE_PROVIDER", label: "Service Provider" },
                { value: "TRADER", label: "Trader" }
              ]))
            )
          ),
          // Address
          h("div", { className: "space-y-3" },
            h("h3", { className: "text-xs font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Address"),
            h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
              field("Address Line", h(Textarea, { value: form.address, onChange: e => set("address", e.target.value), rows: 2 }), 3),
              field("City", h(Input, { value: form.city, onChange: e => set("city", e.target.value) })),
              field("State", h(Input, { value: form.state, onChange: e => set("state", e.target.value) })),
              field("Pincode", h(Input, { value: form.pincode, onChange: e => set("pincode", e.target.value) })),
              field("Country", h(Input, { value: form.country, onChange: e => set("country", e.target.value) }))
            )
          ),
          // Contact
          h("div", { className: "space-y-3" },
            h("h3", { className: "text-xs font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Contact"),
            h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
              field("Contact Person", h(Input, { value: form.contact_person, onChange: e => set("contact_person", e.target.value) })),
              field("Phone", h(Input, { value: form.phone, onChange: e => set("phone", e.target.value) })),
              field("Email", h(Input, { type: "email", value: form.email, onChange: e => set("email", e.target.value) })),
              field("Website", h(Input, { value: form.website, onChange: e => set("website", e.target.value), placeholder: "https://..." }))
            )
          ),
          // Tax / Compliance
          h("div", { className: "space-y-3" },
            h("h3", { className: "text-xs font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Tax & Compliance"),
            h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
              field("GST Number", h(Input, { value: form.gst_number, onChange: e => set("gst_number", e.target.value), placeholder: "29ABCDE1234F1Z5" })),
              field("PAN Number", h(Input, { value: form.pan_number, onChange: e => set("pan_number", e.target.value), placeholder: "ABCDE1234F" })),
              field("MSME Status", sel(form.msme_status, v => set("msme_status", v), [
                { value: "NONE", label: "Not Applicable" },
                { value: "MICRO", label: "Micro Enterprise" },
                { value: "SMALL", label: "Small Enterprise" },
                { value: "MEDIUM", label: "Medium Enterprise" }
              ])),
              form.msme_status !== "NONE" && field("MSME Certificate No.", h(Input, { value: form.msme_certificate, onChange: e => set("msme_certificate", e.target.value) }), 3)
            )
          ),
          // Bank
          h("div", { className: "space-y-3" },
            h("h3", { className: "text-xs font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Bank Details"),
            h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
              field("Bank Name", h(Input, { value: form.bank_name, onChange: e => set("bank_name", e.target.value) })),
              field("Account Number", h(Input, { value: form.bank_account, onChange: e => set("bank_account", e.target.value) })),
              field("IFSC Code", h(Input, { value: form.ifsc_code, onChange: e => set("ifsc_code", e.target.value) }))
            )
          ),
          // Status
          h("div", { className: "space-y-3" },
            h("h3", { className: "text-xs font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Status"),
            h("div", { className: "flex items-center gap-3 p-3 bg-rose-50 rounded-lg border border-rose-200" },
              h("input", { type: "checkbox", checked: !!form.is_blacklisted, onChange: e => set("is_blacklisted", e.target.checked), className: "rounded", "data-testid": "v-blacklist" }),
              h("div", { className: "flex-1" },
                h("p", { className: "text-sm font-semibold text-rose-900" }, "Mark as Blacklisted"),
                h("p", { className: "text-xs text-rose-700" }, "Blacklisted vendors cannot be selected for new procurements")
              )
            ),
            form.is_blacklisted && field("Blacklist Reason", h(Textarea, { value: form.blacklist_reason, onChange: e => set("blacklist_reason", e.target.value), rows: 2, placeholder: "e.g. Performance issues, fraudulent activity..." })),
            field("Notes", h(Textarea, { value: form.notes, onChange: e => set("notes", e.target.value), rows: 2, placeholder: "Internal notes..." }))
          )
        ),
        h(DialogFooter, { className: "gap-2 mt-2" },
          h(Button, { variant: "outline", onClick: () => setOpen(false) }, h(X, { className: "w-4 h-4 mr-1" }), "Cancel"),
          h(Button, { onClick: submit, disabled: saving, className: "bg-slate-900 hover:bg-slate-800", "data-testid": "save-vendor-btn" },
            h(Save, { className: "w-4 h-4 mr-2" }), saving ? "Saving..." : (editingId ? "Update Vendor" : "Create Vendor")
          )
        )
      )
    )
  );
}

export default VendorManagement;
